import React from 'react';
import {ForecastMapService} from '../api';
import {ForecastMapCapabilities} from '../api/types';
import useApiClient from './useApiClient';

type CapabilitiesStatus = 'loading' | 'ready' | 'error';

type CapabilitiesResult = {
  // Which load produced this result; a mismatch means a load is in flight.
  key: number | null;
  status: 'ready' | 'error';
  capabilities: ForecastMapCapabilities | null;
};

/**
 * Fetches the composer capabilities catalog (layer types, colormaps,
 * limits, per-model metrics). Server-cached ~15 min; fetched once per
 * builder mount.
 */
const useForecastMapCapabilities = () => {
  const {fetch} = useApiClient();
  const [loadKey, setLoadKey] = React.useState(0);
  const [result, setResult] = React.useState<CapabilitiesResult>({
    key: null,
    status: 'ready',
    capabilities: null,
  });

  React.useEffect(() => {
    let cancelled = false;
    fetch(ForecastMapService.forecastMapCapabilities())
      .then((capabilities) => {
        if (!cancelled) {
          setResult({key: loadKey, status: 'ready', capabilities});
        }
      })
      .catch(() => {
        if (!cancelled) {
          setResult({key: loadKey, status: 'error', capabilities: null});
        }
      });
    // Drop late responses on unmount/reload.
    return () => {
      cancelled = true;
    };
  }, [fetch, loadKey]);

  const reload = React.useCallback(() => setLoadKey((key) => key + 1), []);

  const loading = result.key !== loadKey;
  const status: CapabilitiesStatus = loading ? 'loading' : result.status;

  return {
    status,
    capabilities: loading ? null : result.capabilities,
    reload,
  };
};

export default useForecastMapCapabilities;
